import { cn } from "@-/com.npmjs.cn";
import { Pressable, View } from "@-/com.npmjs.react-native";
import { Icon } from "@-/dev.improve.haniverse.components.ui.icon";
import { Text } from "@-/dev.improve.haniverse.components.ui.text";
import type { LearnCardViewMode } from "@-/dev.improve.haniverse.lib.learn.deck";
import {
  LEARN_CARD_BINDER_DETAILS_TAB_WIDTH,
  LearnCardBinderTabStrip,
} from "@-/dev.improve.haniverse.components.ui.learn-card-shell";
import { PortalHost } from "@-/com.npmjs.react-native-teleport";

const TABS = [
  {
    icon: "MdLightbulbOutline",
    label: "Learn",
    testID: "learn-card-tab-learn",
    view: "study",
  },
  { icon: "LuBookOpen", label: "Details", testID: "learn-card-tab-details", view: "info" },
] as const;

const DETAILS_TAB_STYLE = { width: LEARN_CARD_BINDER_DETAILS_TAB_WIDTH };

/**
 * The binder tabs along the top of one revealed face, right above its title: Learn (the
 * mnemonics) on the left, icon-only Details (the info) on the right. Each face draws its own
 * strip inside its shell (`LearnFlashcardShell`'s `binderTabs`), so the tabs move, fade and mount
 * with that face and nothing else. The tab of the face's own `view` is CONNECTED to it — same
 * surface, no edge between them — while the other tab stands on the face's top edge. Each tab has
 * rounded top corners and flares into that edge between the tabs; its outside edge aligns with
 * the card. The strip draws that whole top edge, so the face's body leaves it open.
 *
 * Details always shows only its icon; its sections switch inside the card body. A back face's
 * active Learn tab hosts the character's font glyph plus one pinyin section per reading, which
 * that back teleports into `mnemonicsTabHostName` and which draw their own layered flares. The
 * label shows through when the back has no readings (or is not a grapheme's mnemonics). Inactive
 * Learn stays a single labeled tab. Pressing a tab shows that view at once.
 *
 * Details is square and always drawn behind Learn. Learn takes the remaining room, shared equally
 * by the character and every pinyin section without measuring their labels. The tabs lay out by
 * flex, so the host exists from the strip's first render; only the drawn outline needs `width`,
 * the card's, which the card measures once for all of its faces.
 */
export function LearnFlashcardBinderTabs({
  isInteractive,
  mnemonicsTabHostName,
  onSelect,
  view,
  width,
}: {
  isInteractive: boolean;
  /** The back's mnemonics sections teleport into its active Learn tab under this name. */
  mnemonicsTabHostName?: string;
  onSelect: (view: LearnCardViewMode) => void;
  /** The view of the face drawing these tabs: its tab is the active one. */
  view: LearnCardViewMode;
  width: number;
}) {
  return (
    <View
      className="flex-1 flex-row"
      pointerEvents={isInteractive ? "box-none" : "none"}
      testID="learn-card-binder-tabs"
    >
      {width === 0 ? null : (
        <LearnCardBinderTabStrip
          activeIndex={view === "study" ? 0 : 1}
          frontmostIndex={0}
          tabWidths={[
            Math.max(0, width - LEARN_CARD_BINDER_DETAILS_TAB_WIDTH),
            LEARN_CARD_BINDER_DETAILS_TAB_WIDTH,
          ]}
        />
      )}
      {TABS.map((tab) => {
        const isActive = tab.view === view;
        // Learn takes the remaining width. Hosted sections need its full bounds and visible
        // overflow for their concave flares.
        const sizeClassName = tab.view === "study" ? "min-w-0 flex-1" : undefined;
        const sizeStyle = tab.view === "study" ? undefined : DETAILS_TAB_STYLE;
        const label = (
          <>
            <Icon
              className={isActive ? "text-foreground" : "text-muted-foreground/70"}
              name={tab.icon}
              size={13}
            />
            {tab.view === "info" ? null : (
              <Text
                className={cn("text-[11px]", isActive ? "font-medium" : "text-muted-foreground")}
              >
                {tab.label}
              </Text>
            )}
          </>
        );
        if (isActive) {
          return (
            <View className={sizeClassName} key={tab.view} style={sizeStyle} testID={tab.testID}>
              <View
                accessibilityLabel={`${tab.label} view`}
                accessibilityRole="tab"
                accessibilityState={{ selected: true }}
                accessible
                className="flex-1 flex-row items-center justify-center gap-1.5"
              >
                {label}
              </View>
              {mnemonicsTabHostName !== undefined && tab.view === "study" ? (
                <PortalHost className="absolute inset-0" name={mnemonicsTabHostName} />
              ) : null}
            </View>
          );
        }
        return (
          <Pressable
            accessibilityLabel={`${tab.label} view`}
            accessibilityRole="tab"
            accessibilityState={{ selected: false }}
            className={cn("flex-row items-center justify-center gap-1.5", sizeClassName)}
            disabled={!isInteractive}
            key={tab.view}
            onPress={() => onSelect(tab.view)}
            style={sizeStyle}
            testID={tab.testID}
          >
            {label}
          </Pressable>
        );
      })}
    </View>
  );
}
